import React from 'react';
import { DarkMode, LightMode, MenuBook } from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';

type Mode = 'dark' | 'light' | 'sepia';

const options: { value: Mode; label: string; Icon: typeof DarkMode }[] = [
  { value: 'dark', label: 'Karanlık mod', Icon: DarkMode },
  { value: 'light', label: 'Aydınlık mod', Icon: LightMode },
  { value: 'sepia', label: 'Sepia mod', Icon: MenuBook },
];

interface ThemeToggleProps {
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Segmented 3-way theme switcher: Dark / Light / Sepia.
 * Selected option has a pill background; others are muted icons.
 * Use in navbars, settings panels, or anywhere the user should pick a mode.
 */
const ThemeToggle: React.FC<ThemeToggleProps> = ({ size = 'md', className = '' }) => {
  const { theme, setTheme } = useTheme();

  const btnSize = size === 'sm' ? 'w-7 h-7' : 'w-8 h-8';
  const iconSize = size === 'sm' ? 14 : 16;

  return (
    <div
      role="radiogroup"
      aria-label="Tema seçimi"
      className={`inline-flex items-center gap-0.5 p-0.5 rounded-full border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900 ${className}`}
    >
      {options.map(({ value, label, Icon }) => {
        const active = theme === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={label}
            title={label}
            onClick={() => setTheme(value as 'dark' | 'light' | 'sepia')}
            className={`${btnSize} rounded-full flex items-center justify-center transition-colors ${
              active
                ? 'bg-white dark:bg-gray-100 text-gray-900 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
            }`}
          >
            <Icon sx={{ fontSize: iconSize }} />
          </button>
        );
      })}
    </div>
  );
};

export default ThemeToggle;
